"use strict";
/**
 * `RPM-102` rows 3 and 7 — the clinical path reads an account and never a person, and the one
 * surface that DOES read a person joins it outward (`CR-12`, `CR-33`, `SPEC-D-15`).
 *
 * §6's MUST: *"No requirement in §6.1 through §6.8 joins to `people`. The nurse who picks a
 * facility, picks a resident, enters vitals and records an attempt is an **account**"*, and its API
 * row: *"Every guarded route resolves assignments per request against `user.id` and reads no
 * `people` row to do it."*
 *
 * THE SUBJECT, in one sentence: **a server route handler that reads a `people` row on the request
 * path.** Not "the linter".
 *
 * WHY THE SCOPE IS `routes` AND `modules` RATHER THAN `modules/rpm`. §6's Test level cell says "a
 * static check that no route handler under `apps/server/src/modules/rpm/**` joins `people`".
 * Measured on 2026-09-19: `apps/server/src/modules/` holds `facilities` and `patients` and nothing
 * else — there is no `rpm` directory. A check written against that glob reads zero files, passes,
 * and keeps passing for exactly as long as the RPM modules are being written, which is precisely
 * when it is supposed to bite. So the scope is the DIRECTORIES THAT HOLD REQUEST-PATH CODE, walked
 * recursively; `modules/rpm` is inside one of them the moment somebody creates it, with nothing to
 * remember and nothing to update.
 *
 * WHY THERE IS AN EXCEPTION, AND WHY IT IS NOT A HOLE. Row 2 of the same requirement asks the
 * administrative account list to state "no person record linked", which cannot be answered without
 * reading `people`; §6.10's administrative surfaces are where a person is administered. So an
 * administrative handler is enumerated by name with its reason — and the exception carries row 3's
 * obligation rather than dropping it: in an exempted handler an INNER join to `people` is still a
 * finding, because it "would silently omit every account in this shape, including every service
 * principal, from exactly the administrative and audit reports that exist to find them".
 *
 * The entry point REFUSES an empty population rather than reporting a clean run over nothing.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.PERSON_TABLES = exports.NOT_ON_THE_REQUEST_PATH = exports.ADMINISTRATIVE_HANDLERS = exports.HANDLER_DIRECTORIES = void 0;
exports.findPeopleReadsInHandlers = findPeopleReadsInHandlers;
/**
 * The scope: the server's whole source tree, walked recursively.
 *
 * It was `routes` and `modules` alone, and that left a hole a review found: a `people` read placed
 * in a shared helper under `db/`, `lib/` or `plugins/` and merely CALLED from a handler is still a
 * read on the request path, and the narrower scope could not see it. Widening costs two enumerated
 * exceptions, below, and closes it. `apps/server/src/modules/rpm` is inside this the moment
 * somebody creates it, with nothing to remember.
 */
exports.HANDLER_DIRECTORIES = ["apps/server/src"];
/**
 * Handlers that MAY read a person, each with the reason, keyed by repo-relative path.
 *
 * `findPeopleReadsInHandlers` reports an entry whose file no longer reads a person, because an
 * exception nobody prunes is an exception nobody reads. Adding an entry is a decision that needs a
 * journal line, the way §9.3's eight excluded tables do.
 */
exports.ADMINISTRATIVE_HANDLERS = {
    "apps/server/src/routes/users.ts": "RPM-092's administrative account list, and the one surface RPM-102 row 2 requires to state `no person record linked`. It is §6.10, not §6.1-§6.8. Its join MUST be outward, which is row 3 and which this check asserts rather than trusts.",
};
/**
 * Files inside the scope that are not on the request path at all, each with the reason.
 *
 * A seed CREATES the person register; it runs from a CLI and serves no request, so §6's "every
 * guarded route ... reads no `people` row" says nothing about it. Reported when one stops reading a
 * person, the same as the administrative list, so neither map can quietly go inert.
 */
exports.NOT_ON_THE_REQUEST_PATH = {
    "apps/server/src/db/seed.ts": "The seed. It writes the `people` rows and affiliations the local and test environments need (§7.7(i)), from a CLI. It serves no request and is never imported by a handler.",
};
// `apps/server/src/db/seed-platform-principal.ts` is deliberately NOT here. It names `people` in
// its comments and reads no row, so an entry for it would excuse nothing — and `staleExceptions`
// said so within a minute of the entry being written, which is the map working.
/** The two tables this requirement keeps off the clinical path, in both spellings the stack uses. */
exports.PERSON_TABLES = [
    { drizzle: "people", sql: "people" },
    { drizzle: "personTenantAffiliations", sql: "person_tenant_affiliations" },
];
/** A Drizzle read or join naming the identifier: `.from(people)`, `.leftJoin(people, …)`, … */
const drizzleRead = (id) => new RegExp(`\\.(from|innerJoin|leftJoin|rightJoin|fullJoin|join|update|insert|delete)\\s*\\(\\s*${id}\\b`);
/** A raw-SQL read naming the table: `from people`, `join person_tenant_affiliations`, … */
const sqlRead = (table) => new RegExp(`\\b(from|join|into|update)\\s+(public\\.)?"?${table}"?\\b`, "i");
/** An import of the table from the schema package: how a handler reaches one at all. */
const importsTable = (id) => new RegExp(`import[^;]*\\b${id}\\b[^;]*from\\s*["']@chartvn/schema/db["']`, "s");
/** An INNER join to the table, in either spelling. A bare `JOIN` in SQL is an inner join. */
const innerJoin = (drizzle, sql) => new RegExp(`\\.innerJoin\\s*\\(\\s*${drizzle}\\b` +
    `|(?<!\\b(?:left|right|full)(?:\\s+outer)?\\s{1,8})\\bjoin\\s+(public\\.)?"?${sql}"?\\b`, "i");
/**
 * Every line on the request path that reads a person, judged by where the file sits.
 *
 * @throws when no file was scanned. A check whose walk matches nothing and passes is the failure
 * this one exists to avoid being.
 */
function findPeopleReadsInHandlers(sources) {
    if (sources.length === 0)
        throw new Error(`HANDLER_READS_PEOPLE: no handler file was scanned. The scope is ${exports.HANDLER_DIRECTORIES.join(" and ")}, walked recursively; matching nothing means the walk is wrong, not that the handlers are clean.`);
    const findings = [];
    const readsAPerson = new Set();
    let lines = 0;
    for (const { path, source } of sources) {
        const administrative = Object.hasOwn(exports.ADMINISTRATIVE_HANDLERS, path);
        const offPath = Object.hasOwn(exports.NOT_ON_THE_REQUEST_PATH, path);
        const all = source.split("\n");
        lines += all.length;
        for (const { drizzle, sql } of exports.PERSON_TABLES) {
            // The import is matched against the WHOLE source, not line by line: Prettier wraps a long
            // import across several lines, and then no single line carries both the identifier and the
            // module specifier. The finding is reported on the first line that names the identifier.
            const importedAt = importsTable(drizzle).test(source)
                ? all.findIndex((text) => new RegExp(`\\b${drizzle}\\b`).test(text))
                : -1;
            all.forEach((text, i) => {
                const reads = i === importedAt || drizzleRead(drizzle).test(text) || sqlRead(sql).test(text);
                if (!reads)
                    return;
                readsAPerson.add(path);
                if (offPath)
                    return;
                if (!administrative) {
                    findings.push({
                        code: "HANDLER_READS_PEOPLE",
                        path,
                        line: i + 1,
                        detail: `reads \`${sql}\` on the clinical request path; §6.1-§6.8 read an account and never a person (RPM-102, CR-12). If this file is an administrative surface, enumerate it in ADMINISTRATIVE_HANDLERS with the reason; if it serves no request at all, in NOT_ON_THE_REQUEST_PATH.`,
                    });
                    return;
                }
                if (innerJoin(drizzle, sql).test(text))
                    findings.push({
                        code: "ADMIN_READ_IS_AN_INNER_JOIN",
                        path,
                        line: i + 1,
                        detail: `joins \`${sql}\` inward. RPM-102 row 3: an inner join "would silently omit every account in this shape, including every service principal, from exactly the administrative and audit reports that exist to find them". Use an outer join.`,
                    });
            });
        }
    }
    const staleExceptions = [
        ...Object.keys(exports.ADMINISTRATIVE_HANDLERS),
        ...Object.keys(exports.NOT_ON_THE_REQUEST_PATH),
    ].filter((p) => !readsAPerson.has(p));
    return { findings, inspected: sources.length, lines, staleExceptions };
}
//# sourceMappingURL=people-joins.js.map